import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { concatErrorCache } from './cacheErrorHandling';

export const defaultTags = ['UNAUTHORIZED', 'UNKNOWN_ERROR'] as const;
export type DefaultTags = 'UNAUTHORIZED' | 'UNKNOWN_ERROR';

/**
 * An individual cache item
 */
export interface CacheItem<T, ID> {
  type: T;
  id: ID;
}

/**
 * A list of cache items, including a LIST entity cache
 */
export type CacheList<T, ID> = (
  | CacheItem<T, 'LIST'>
  | CacheItem<T, ID>
  | DefaultTags
)[];

export type CacheListWithPagination<T, ID> = (
  | CacheItem<T, 'PAGED-LIST'>
  | CacheItem<T, ID>
  | DefaultTags
)[];

type InnerProvidesList<T> = <
  Results extends { id: unknown }[],
  Error extends FetchBaseQueryError
>(
  results: Results | undefined,
  error: Error | undefined
) => CacheList<T, Results[number]['id']>;

export const providesListTags =
  <T extends string>(type: T): InnerProvidesList<T> =>
  (results, error) => {
    // is result available?
    if (results) {
      // successful query
      return [
        { type, id: 'LIST' },
        ...results.map(({ id }) => ({ type, id } as const))
      ];
    }
    // Received an error, include an error cache item to the cache list
    return concatErrorCache([{ type, id: 'LIST' }], error);
  };

/**
 * HOF to create an entity cache for a single item using the query argument as the ID.
 *
 * @example
 * ```ts
 * cacheByIdArg('Todo')({ id: 5, message: 'walk the fish' }, undefined, 5)
 * // returns:
 * // [{ type: 'Todo', id: 5 }]
 * ```
 */
export const cacheByQueryArg =
  <T extends string>(type: T) =>
  <ID, Result = undefined, Error = undefined>(
    result: Result,
    error: Error,
    id: ID
  ): readonly [CacheItem<T, ID>] =>
    [{ type, id }] as const;

/**
 * HOF to create an entity cache for a single item using the query argument as the ID, where the target property on the query object is provided.
 *
 * @example
 * ```ts
 * cacheByIdArg('Todo', 'siteId')({ siteId: 'ruler', message: 'walk the fish' }, undefined, undefined)
 * // returns:
 * // [{ type: 'Todo', id: 5 }]
 * ```
 */
export const cacheByQueryArgProperties =
  <T extends string>(type: T, targetPropNames: string[]) =>
  <Arg extends Record<string, any>, Result = undefined, Error = undefined>(
    result: Result,
    error: Error,
    arg: Arg
  ): readonly [CacheItem<T, string>] =>
    [{ type, id: getGenerateIdForQuery(targetPropNames, arg) }] as const;

const getGenerateIdForQuery = (
  targetPropNames: string[],
  argumentWithProperties: Record<string, any>
) => {
  const keys = targetPropNames.map((prop) => argumentWithProperties[prop]);
  return keys.join('-');
};

/**
 * HOF to invalidate the 'UNAUTHORIZED' type cache item.
 */
export const invalidatesUnauthorized =
  () =>
  <Arg = undefined, Result = undefined, Error = undefined>(
    result: Result,
    error: Error,
    arg: Arg
  ): ['UNAUTHORIZED'] =>
    ['UNAUTHORIZED'];

/**
 * HOF to invalidate the 'UNKNOWN_ERROR' type cache item.
 */
export const invalidatesUnknownErrors =
  () =>
  <Arg = undefined, Result = undefined, Error = undefined>(
    result: Result,
    error: Error,
    arg: Arg
  ): ['UNKNOWN_ERROR'] =>
    ['UNKNOWN_ERROR'];
